/**
 * Pending-writes gate for the async auth storage adapter.
 *
 * On native, `hybridStorage.setItem` awaits `@capacitor/preferences.set`,
 * which is not synchronous with the app going background. If Android
 * force-kills the app while a Supabase token rotation write is in flight,
 * the next cold boot reads an already-consumed refresh token and the
 * server returns `refresh_token_not_found` — the user is logged out.
 *
 * We close that window by:
 *   - counting every in-flight storage write here, and
 *   - having `initNative` listen for `App.pause` and await
 *     `awaitPendingWrites()` before returning, which yields long enough
 *     for the SharedPreferences commit to flush.
 *
 * This module is process-local, sync, and has zero deps so it can be
 * imported from anywhere.
 */

let inFlight = 0;
let resolveIdle: (() => void) | null = null;
let idlePromise: Promise<void> = Promise.resolve();

const armIdle = () => {
  idlePromise = new Promise<void>((r) => { resolveIdle = r; });
};

export const beginWrite = (): void => {
  if (inFlight === 0) armIdle();
  inFlight++;
};

export const endWrite = (): void => {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0 && resolveIdle) {
    const r = resolveIdle;
    resolveIdle = null;
    r();
  }
};

/**
 * Resolves when no auth-storage write is in flight. Bounded by `timeoutMs`
 * so a pathological hang never blocks app pause indefinitely.
 */
export const awaitPendingWrites = async (timeoutMs = 1500): Promise<void> => {
  if (inFlight === 0) return;
  await Promise.race([
    idlePromise,
    new Promise<void>((r) => setTimeout(r, timeoutMs)),
  ]);
};

export const pendingWriteCount = (): number => inFlight;
